/**
 * `TestKitsView` — the Testing area's front page (founder request
 * 2026-10-31: "organise the ops for the testing area for all the
 * things we have and will build").
 *
 * One card per test kit: what it exercises, how to run it, and the
 * one button that starts it (a download for a paste-in checklist, a
 * link for a console page). Kits that need a role the signed-in
 * staff member does not hold are listed without their button so the
 * page still shows the whole inventory.
 *
 * The assistant-connection checklist ships as a markdown file next
 * to this view (`kits/`), a byte-for-byte copy of the runbook in
 * `docs/runbooks/`; a test pins the two together.
 */

import { Download, ExternalLink, FlaskConical } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { useAuth } from '../../app/auth-context.js';
import type { SetView, View } from '../../app/view-router.js';
import { Card } from '../../components/atoms/Card.js';
import { canView } from '../../lib/roles.js';
import { triggerDownload } from '../../lib/report-export.js';
import { useC } from '../../lib/theme.js';

import assistantMcpChecklist from './kits/assistant-mcp-smoke-checklist.md?raw';

const monoFont = "'JetBrains Mono', monospace";
const serifFont = "'Instrument Serif', serif";
const sansFont = "'Geist', sans-serif";

export const ASSISTANT_MCP_CHECKLIST_FILENAME = 'assistant-mcp-smoke-checklist.md';
export const ASSISTANT_MCP_CHECKLIST = assistantMcpChecklist;

interface KitAction {
  readonly kind: 'download' | 'open';
  readonly label: string;
  /** Page to open (`kind: 'open'`). */
  readonly view?: View;
}

interface Kit {
  readonly id: string;
  readonly title: string;
  readonly covers: string;
  readonly steps: readonly string[];
  readonly needs: string;
  readonly action: KitAction;
}

export const KITS: readonly Kit[] = [
  {
    id: 'assistant-mcp',
    title: 'Assistant connection',
    covers:
      'Every tool a merchant’s own Claude or ChatGPT can call through Account → Connected apps: 13 reads, 2 block tools, the readiness run, 2 agency tools, and the error paths.',
    steps: [
      'Connect an assistant to a test account with every permission ticked (Account → Connected apps).',
      'Download the checklist and paste the block under “Prompt for the assistant” into the chat.',
      'Read the PASS / FAIL table it returns. A tool that is missing was not ticked at approval.',
    ],
    needs: 'A Sill account with at least one site; owner or admin for the block tools.',
    action: { kind: 'download', label: 'Download checklist (.md)' },
  },
  {
    id: 'smoke',
    title: 'Agent smoke-test harness',
    covers:
      'Sill’s own test agent calls each skill a merchant site exposes (browse, availability, order status…) and records what came back.',
    steps: [
      'Open the account in Accounts and switch to its Test tab.',
      'Turn on the smoke opt-in, pick a site, click Run smoke now.',
      'Follow the run on Smoke Runs; failures name the skill and the response.',
    ],
    needs: 'Support role or above. The account must have opted in.',
    action: { kind: 'open', label: 'Open Smoke Runs', view: 'smoke' },
  },
  {
    id: 'protocol-tests',
    title: 'Protocol test agents',
    covers:
      'Drives one protocol end to end against a chosen site: A2A card, MCP, PAP, UCP. Shows the wire exchange and the verdict.',
    steps: ['Pick a site and a protocol.', 'Click Run (admin or above).', 'Read the exchange and the verdict.'],
    needs: 'Support to view, admin to run.',
    action: { kind: 'open', label: 'Open Protocol tests', view: 'protocol-tests' },
  },
  {
    id: 'ucp-checkout',
    title: 'UCP checkout test',
    covers: 'A full agent checkout through the site’s UCP endpoint on the Stripe test rail: cart, payment mandate, order, webhook.',
    steps: [
      'Open the account in Accounts and switch to its Test tab.',
      'Click Run UCP checkout test next to the site.',
      'Check the order appears under the site’s held or settled orders.',
    ],
    needs: 'Support role or above. The site needs a Stripe test-mode connection.',
    action: { kind: 'open', label: 'Open Accounts', view: 'accounts' },
  },
  {
    id: 'testsuite',
    title: 'CI test suite',
    covers: 'The most recent GitHub Actions run of every workflow on main, failures first. Escalate a failure into an issue from the row.',
    steps: ['Open Test Suite.', 'Failing workflows sort to the top; open the run on GitHub from the row.'],
    needs: 'Any role. Read-only.',
    action: { kind: 'open', label: 'Open Test Suite', view: 'testsuite' },
  },
  {
    id: 'install-fleet',
    title: 'Install fleet dry run',
    covers: 'Rehearses a fleet-wide Cloudflare Worker redeploy without touching merchant accounts and lists what would change.',
    steps: ['Open Install fleet.', 'Click Dry run and read the plan before any real redeploy.'],
    needs: 'Admin or above.',
    action: { kind: 'open', label: 'Open Install fleet', view: 'install-fleet' },
  },
];

export function TestKitsView({ setView }: { readonly setView: SetView }): JSX.Element {
  const C = useC();
  const auth = useAuth();
  const role = auth.state.kind === 'authenticated' ? auth.state.staff.role : undefined;

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-8 py-6 sm:py-10">
      <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 10 }}>
        QA console
      </div>
      <div className="mb-6 sm:mb-8 min-w-0">
        <h1 style={{ fontFamily: serifFont, fontSize: 'clamp(28px, 6vw, 44px)', lineHeight: 1, color: C.text, letterSpacing: -1.5, margin: 0 }}>
          Test kits
        </h1>
        <div style={{ fontFamily: sansFont, fontSize: 13, color: C.body, marginTop: 10, lineHeight: 1.5, maxWidth: 640 }}>
          Every way to exercise Sill end to end, in one place. Each kit says what it covers, how to run it and what it needs. Add a card here when a new surface gets a test.
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" role="list" aria-label="Test kits">
        {KITS.map((kit) => (
          <KitCard
            key={kit.id}
            kit={kit}
            icon={FlaskConical}
            allowed={kit.action.kind === 'download' || (kit.action.view !== undefined && canView(role, kit.action.view))}
            onRun={() => {
              if (kit.action.kind === 'download') {
                triggerDownload(ASSISTANT_MCP_CHECKLIST_FILENAME, 'text/markdown', ASSISTANT_MCP_CHECKLIST);
              } else if (kit.action.view !== undefined) {
                setView(kit.action.view);
              }
            }}
          />
        ))}
      </div>
    </div>
  );
}

function KitCard({
  kit,
  icon: Icon,
  allowed,
  onRun,
}: {
  readonly kit: Kit;
  readonly icon: LucideIcon;
  readonly allowed: boolean;
  readonly onRun: () => void;
}): JSX.Element {
  const C = useC();
  return (
    <Card role="listitem" data-testid={`kit-${kit.id}`} style={{ padding: 20 }} className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <Icon size={14} aria-hidden="true" style={{ color: C.muted }} />
        <h2 style={{ fontFamily: serifFont, fontSize: 22, lineHeight: 1.1, color: C.text, margin: 0, letterSpacing: -0.5 }}>
          {kit.title}
        </h2>
      </div>
      <p style={{ fontFamily: sansFont, fontSize: 13, color: C.body, lineHeight: 1.55, margin: 0 }}>{kit.covers}</p>
      <div>
        <Kicker>How to run</Kicker>
        <ol style={{ fontFamily: sansFont, fontSize: 13, color: C.body, lineHeight: 1.55, margin: 0, paddingLeft: 18 }}>
          {kit.steps.map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ol>
      </div>
      <div>
        <Kicker>Needs</Kicker>
        <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, lineHeight: 1.5 }}>{kit.needs}</div>
      </div>
      <div className="mt-auto pt-1">
        {allowed ? (
          <button
            type="button"
            onClick={onRun}
            data-testid={`kit-${kit.id}-run`}
            className="inline-flex items-center gap-2"
            style={{
              background: C.lime,
              color: C.ink,
              border: `1px solid ${C.lime}`,
              borderRadius: 2,
              padding: '9px 14px',
              minHeight: 38,
              fontFamily: monoFont,
              fontSize: 11,
              letterSpacing: 1.2,
              fontWeight: 500,
              textTransform: 'uppercase',
              cursor: 'pointer',
            }}
          >
            {kit.action.kind === 'download' ? <Download size={13} aria-hidden="true" /> : <ExternalLink size={13} aria-hidden="true" />}
            {kit.action.label}
          </button>
        ) : (
          <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 1.2, textTransform: 'uppercase' }}>
            Not available for your role
          </div>
        )}
      </div>
    </Card>
  );
}

function Kicker({ children }: { readonly children: string }): JSX.Element {
  const C = useC();
  return (
    <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 1.6, textTransform: 'uppercase', marginBottom: 4 }}>
      {children}
    </div>
  );
}
